import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Outlet } from "react-router";

import { type Corporation, fetchCorporations } from "@/api/corporations";
import { useScrollToHash } from "@/lib/useScrollToHash";

import { Header } from "./Header";
import { useShellAttributes } from "./shellPreferences";
import { Sidebar } from "./Sidebar";
import { SubNav } from "./SubNav";
import "./AppShell.css";

/**
 * De applicatieschil (stijl A, 28-10-2026): navy zijbalk links, een lichte
 * kop zonder balk boven, inhoud op de warme ondergrond. Boven elk scherm de
 * tabbalk van het onderdeel (`SubNav`). Elk scherm rendert binnen `<Outlet />`.
 *
 * De corporatie komt uit de API en niet meer uit een fixture. Zolang er niets
 * echts in stond was "Woonstichting Fixture" de juiste keuze — herkenbaar nep
 * is beter dan aannemelijk fout — maar boven het werkelijke bezit van een
 * corporatie is een verzonnen naam misleidend.
 *
 * De keuze staat in component-state. Zodra de schermen `corporation_id`
 * meesturen verhuist hij naar een store (zustand zit al in de toolchain), omdat
 * hij dan ook de querysleutels van TanStack Query bepaalt.
 */
export function AppShell() {
  // Menu-items die naar een sectie wijzen (Deelportefeuilles, v4.0).
  useScrollToHash();
  // Maat, dichtheid en zijbalk als attribuut op <html> (ronde 3, drie maten).
  const { collapsed, setCollapsed } = useShellAttributes();
  const query = useQuery({
    queryKey: ["corporations"],
    queryFn: fetchCorporations,
    // De lijst verandert niet tijdens een sessie: een corporatie erbij is een
    // onboarding, geen schermhandeling.
    staleTime: Infinity,
  });
  const corporations = query.data ?? [];
  const [corporationId, setCorporationId] = useState<string | null>(null);
  const selected: Corporation | null =
    corporations.find((option) => option.id === corporationId) ?? corporations[0] ?? null;

  return (
    <div className="atl-shell">
      <a className="atl-skip-link" href="#hoofdinhoud">
        Naar de inhoud
      </a>
      {/* Op een smal scherm staat het logo van de corporatie in het merkblok
          van de zijbalk (FB-065); valt er te kiezen, dan blijft het bij de
          keuzelijst in de header. */}
      <Sidebar
        corporation={corporations.length > 1 ? null : selected}
        loading={query.isPending}
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
      />
      <div className="atl-shell__main">
        <Header
          corporations={corporations}
          selected={selected}
          loading={query.isPending}
          onSelect={setCorporationId}
        />
        <main className="atl-shell__content" id="hoofdinhoud" tabIndex={-1}>
          <div className="atl-shell__container">
            <SubNav />
            <Outlet context={selected} />
          </div>
        </main>
      </div>
    </div>
  );
}
